import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import Select from "react-select";
import {
  AJOUTER_PRESTATAIRE,
  LISTE_COMMUNE_SANS_PAGINATION,
  LISTE_PRESTATAIRE,
} from "../../Utils/constant";
import { find, store } from "../../services/service";
import { useStoreApp } from "../../store/storeApp";

export const AddPrestataire = ({ item = null, onClose = null }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm();
  const [isSubmit, setIsSubmit] = useState(false);
  const [select, setSelect] = useState([]);
  const prestataire = useStoreApp();
  const [communes, setCommunes] = useState([]);
  const [loadingCommunes, setLoadingCommunes] = useState(false);

  // Ref sur le dialog parent — le portal y sera attaché,
  // ce qui place le menu DANS le stacking context du <dialog>
  const [dialogEl, setDialogEl] = useState(null);
  const containerRef = useRef(null);

  const isEditing = !!item;

  useEffect(() => {
    // Remonte jusqu'au <dialog> ancêtre le plus proche
    let el = containerRef.current;
    while (el && el.tagName !== "DIALOG") el = el.parentElement;
    setDialogEl(el ?? document.body);
  }, []);

  const submit = (data) => {
    setIsSubmit(true);
    const endpoint = isEditing ? `${AJOUTER_PRESTATAIRE}` : AJOUTER_PRESTATAIRE;
    store(endpoint, "application/json", {
      ...data,
      id: item?.id,
      isEligibleEntreprise:
        data.isEligibleEntreprise === true ||
        data.isEligibleEntreprise === "on",
      listesCommunes: select.map((s) => s.value),
    })
      .then((res) => {
        setIsSubmit(false);
        if (res.status === 200) {
          prestataire.getData(
            LISTE_PRESTATAIRE,
            { page: 0, param: "", size: 10 },
            "",
          );
          if (onClose) onClose();
          else document.getElementById("close_prestataire").click();
          reset();
          toast.success(
            isEditing ? "Prestataire modifié" : "Prestataire enregistré",
          );
        }
      })
      .catch((err) => {
        setIsSubmit(false);
        toast.error(err.response.data.donnee);
      });
  };

  useEffect(() => {
    if (isEditing && item) {
      setValue("designation", item.designation);
      setValue("isEligibleEntreprise", item.isEligibleEntreprise === true);
      // Initialiser les communes sélectionnées
      if (item.communes && item.communes.length > 0) {
        const selectedCommunes = item.communes.map((c) => ({
          value: c.id,
          label: c.designation,
        }));
        setSelect(selectedCommunes);
      }
    }
  }, [item, isEditing, setValue]);

  useEffect(() => {
    setLoadingCommunes(true);
    find(`${LISTE_COMMUNE_SANS_PAGINATION}`)
      .then((res) =>
        setCommunes(res?.data?.data ?? res?.data?.donnee ?? res?.data ?? []),
      )
      .catch((err) => {
        toast.error(
          err?.response?.data?.donnee || "Erreur chargement communes",
        );
        setCommunes([]);
      })
      .finally(() => setLoadingCommunes(false));
  }, []);

  const communeData = communes.map((item) => ({
    value: item?.id,
    label: item?.designation,
  }));

  const inputCls =
    "w-full h-10 px-3 border border-gray-200 rounded-lg text-sm text-gray-900 bg-gray-50 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100 transition";

  return (
    <form
      ref={containerRef}
      onSubmit={handleSubmit(submit)}
      className="space-y-4"
    >
      {/* Désignation */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Nom du prestataire <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="Désignation"
          className={inputCls}
          {...register("designation", { required: true })}
        />
        {errors.designation && (
          <p className="text-xs text-red-500 mt-1">Ce champ est obligatoire</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Communes {item === null && <span className="text-red-500">*</span>}
        </label>
        <Select
          isMulti
          isLoading={loadingCommunes}
          onChange={(e) => setSelect(e)}
          options={communeData}
          placeholder="Sélectionner des communes…"
          noOptionsMessage={() => "Aucune commune disponible"}
          loadingMessage={() => "Chargement…"}
          // Portal attaché au <dialog> lui-même → reste dans son stacking context
          menuPortalTarget={dialogEl}
          menuPosition="fixed"
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base, state) => ({
              ...base,
              minHeight: 40,
              borderRadius: 8,
              borderColor: state.isFocused ? "#FFCB05" : "#e5e7eb",
              boxShadow: state.isFocused
                ? "0 0 0 2px rgba(255,203,5,0.2)"
                : "none",
              backgroundColor: "#f9fafb",
              fontSize: 13,
              "&:hover": { borderColor: "#d1d5db" },
            }),
            option: (base, state) => ({
              ...base,
              fontSize: 13,
              backgroundColor: state.isSelected
                ? "#FFCB05"
                : state.isFocused
                  ? "#f9fafb"
                  : "#fff",
              color: state.isSelected ? "#111" : "#374151",
            }),
            multiValue: (base) => ({
              ...base,
              borderRadius: 6,
            }),
            multiValueLabel: (base) => ({
              ...base,
              fontSize: 12,
              fontWeight: 500,
            }),
            multiValueRemove: (base) => ({
              ...base,
              color: "#92400e",
              ":hover": { backgroundColor: "#fde68a", color: "#78350f" },
            }),
            placeholder: (base) => ({
              ...base,
              color: "#9ca3af",
              fontSize: 13,
            }),
          }}
        />
      </div>

      {/* isEligibleEntreprise */}
      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-yellow-400 focus:ring-yellow-400"
            {...register("isEligibleEntreprise")}
          />
          <span className="text-xs font-medium text-gray-700">
            Entreprise éligible
          </span>
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmit}
        className={`w-full h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition ${
          isSubmit
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-yellow-400 hover:bg-yellow-300 text-gray-900"
        }`}
      >
        {isSubmit ? (
          <>
            <span className="loading loading-spinner loading-xs" />{" "}
            Enregistrement…
          </>
        ) : (
          "Enregistrer"
        )}
      </button>
    </form>
  );
};
